import React from 'react';
import { useDispatch } from 'react-redux';
import { useAppSelector } from '../../hooks/use-app-selector';
import { Flex, Space, Typography, Dropdown, Avatar, Grid } from 'antd';
import type { MenuProps } from 'antd';
import {
  GiftOutlined,
  DownOutlined,
  UserOutlined,
  LogoutOutlined,
  SettingOutlined,
  CompassOutlined,
  ApartmentOutlined,
  EnvironmentOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import styled from 'styled-components';

import { openCelloWidget } from '../../utils';
import { t } from '../../utils/i18n';
import {
  CELLO_PRODUCT_ID,
  DEFAULT_INTERCOM_PRODUCT_TOUR_ID,
} from '../../constants';
import EVENT_BUS_EVENTS from '../../constants/event-bus-events';
import { useGlobals } from '../../globals-context';
import { changeLocation, clickLogout } from '../../actions';

const { useBreakpoint } = Grid;

const ACTIVE = 'active';

const StyledDropdown = styled(Dropdown)`
  .menu-user-profile {
    .ant-v5-dropdown-menu-item {
      height: 40px;

      .anticon {
        color: rgba(0, 0, 0, 0.45) !important;
      }
    }
  }
`;

type UserProfileProps = {
  homeHelpCenterLink: string | null;
};

export function UserProfile({ homeHelpCenterLink }: UserProfileProps) {
  const dispatch = useDispatch();
  const screens = useBreakpoint();
  const { eventBus, trackMethod } = useGlobals();

  const currentMerchant = useAppSelector((state) => state.currentMerchant);
  const currentAccount = useAppSelector((state) => state.currentAccount);

  const {
    roles,
    displayName,
    avatarUrl,
    color: userPreferredColor,
    locale,
  } = currentAccount || {};
  const { uiVisibility, name: merchantName } = currentMerchant || {};
  const {
    user_profile: { company: companyPermission, settings: settingPermission },
  } = uiVisibility;

  const profileMenuItems: MenuProps['items'] = [
    {
      key: 'user_settings',
      label: t('button.account.title'),
      icon: <UserOutlined />,
    },

    ...(companyPermission === ACTIVE
      ? [
          {
            key: 'company_settings',
            label: t('button.company.title'),
            icon: <ApartmentOutlined />,
          },
        ]
      : []),

    ...(settingPermission === ACTIVE
      ? [
          {
            key: 'booking_settings',
            label: t('button.application-settings.title'),
            icon: <SettingOutlined />,
          },
        ]
      : []),

    ...(homeHelpCenterLink
      ? [
          {
            key: 'help_center',
            label: t('button.help-center.title'),
            icon: <QuestionCircleOutlined />,
          },
        ]
      : []),

    ...((window as any)?.Intercom && locale?.includes('de')
      ? [
          {
            key: 'product_tour',
            label: t('button.product-tour.title'),
            icon: <CompassOutlined />,
          },
        ]
      : []),

    ...(Array.isArray(roles) && roles.length > 1
      ? [
          {
            key: 'locations',
            label: t('button.location.title'),
            icon: <EnvironmentOutlined />,
          },
        ]
      : []),

    ...(CELLO_PRODUCT_ID
      ? [
          {
            key: 'refer_earn',
            label: t('button.referral-and-earn.title'),
            icon: <GiftOutlined />,
            onClick: openCelloWidget,
          },
        ]
      : []),

    {
      type: 'divider',
    },
    {
      key: 'sign_out',
      label: t('button.logout.title'),
      icon: <LogoutOutlined />,
    },
  ];

  const handleOnMenuClicked = ({ key }: { key: string }) => {
    let route = '';

    trackMethod('top_menu_item_clicked', {
      platform: 'booking',
      location: 'user_profile',
      menu_item: key,
    });

    switch (key) {
      case 'user_settings':
        route = '/user-profile';
        break;
      case 'company_settings':
        route = '/settings';
        break;
      case 'booking_settings':
        route = '/application-settings';
        break;
      case 'locations':
        route = '/locations';
        break;
      case 'product_tour':
        (window as any)?.Intercom?.(
          'startTour',
          DEFAULT_INTERCOM_PRODUCT_TOUR_ID
        );
        return;
      case 'referral_earn':
        openCelloWidget();
        break;
      case 'help_center':
        if (homeHelpCenterLink) {
          window.open(homeHelpCenterLink, '_blank');
        }
        return;
      case 'sign_out':
        dispatch(clickLogout());
        break;
      default:
        break;
    }

    // TODO: Change this to use router later once router is updated
    if (route) {
      dispatch(changeLocation(route));
      eventBus.emit(EVENT_BUS_EVENTS.NAVIGATE, { payload: { route } });
    }
  };

  return (
    <StyledDropdown
      arrow
      trigger={['click']}
      placement="bottomRight"
      menu={{
        items: profileMenuItems,
        onClick: handleOnMenuClicked,
        className: 'menu-user-profile',
      }}
      getPopupContainer={() =>
        document.getElementById('user-profile') || document.body
      }
    >
      <Space
        id="user-profile"
        size="small"
        direction="horizontal"
        style={{ cursor: 'pointer', width: '100%' }}
        align="center"
      >
        <Avatar
          size={38}
          src={avatarUrl || undefined}
          style={{ backgroundColor: userPreferredColor || '#a2a2a2' }}
          icon={<UserOutlined />}
        />
        {screens.md && (
          <>
            <Flex
              vertical
              style={{
                width: screens.lg ? 'fit-content' : 'auto',
                maxWidth: screens.lg ? 'none' : '180px',
                minWidth: 0,
              }}
            >
              <Typography.Text
                style={{ fontSize: 14 }}
                ellipsis={{ tooltip: true }}
              >
                {displayName || ''}
              </Typography.Text>
              <Typography.Text
                type="secondary"
                style={{ fontSize: 11 }}
                ellipsis={{ tooltip: true }}
              >
                {merchantName || ''}
              </Typography.Text>
            </Flex>
            <DownOutlined style={{ fontSize: 12, color: '#64748B' }} />
          </>
        )}
      </Space>
    </StyledDropdown>
  );
}
